import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import merge from 'deepmerge'
import { createBasicSelector } from 'util/redux'
import { emptyObj, removeNullValues } from 'util/objects'
import {
  selectWidgets,
  selectWidgetsBootstrapped,
  selectWidgetsWithChat,
} from 'ducks/widgets/selectors'
import { selectFolders } from 'selectors/folders_base'
import { selectRulesTotal } from 'ducks/rules'
import { selectCustomerRatingSettingsCountIfLoaded } from 'ducks/customerRatingSettings'
import { selectCurrentUserIsAdminOrOwner } from 'ducks/currentUser/selectors/base'
import {
  USAGE_INBOX_CANNED_REPLIES,
  USAGE_INBOX_CHANNELS,
  USAGE_INBOX_FOLDERS,
  USAGE_INBOX_MAILBOXES,
  USAGE_INBOX_RULES,
  USAGE_INBOX_SATISFACTIONS_RATINGS,
  USAGE_INBOX_WIDGETS,
  USAGE_KB_ARTICLES,
  USAGE_KB_KBS,
  USAGE_INBOX_TEAMS,
} from 'ducks/billing/usageTypes'
import { selectCannedReplyTemplatesTotalCount } from 'ducks/cannedReplies'
import { selectKnowledgeBaseTotalOrNull } from 'subapps/kb/selectors/knowledge_bases'
import { selectNonDeletedArticlesCount } from 'subapps/kb/selectors/articles'
import { selectMailboxIds } from 'selectors/mailboxes_base'
import {
  selectDemoMailboxId,
  selectInaccessibleMailboxesIds,
} from 'selectors/mailboxes'
import { selectGroups } from 'selectors/app/groups'
import { isDefined } from 'util/nullOrUndefinedChecks'
import selectBillingUsage from './selectBillingUsage'
import {
  selectCurrentInbox,
  selectCurrentKb,
  selectHasPendingDowngrade,
  selectPendingDowngradePlans,
} from './index'
import { billingUsageKeyForProductFeature } from '../featureTypes'
import { INBOX, KB } from '../productTypes'
import { isPlanLimitsExceeded } from '../util'

const byProductCacheKey = (_state, product) => product || 'unknown'
const byProductFeatureCacheKey = (_state, product, feature) =>
  `${product || 'unknown'}-${feature || 'unknown'}`

export const selectBillingUsageForProduct = createCachedSelector(
  selectBillingUsage,
  (_state, product) => product,
  (usage, product) => {
    if (product === INBOX && usage[product]) {
      // Include kb usage in inbox usage for inbox plans that has max_kbs feature
      // and need to calculate kb's usage using isPlanLimitsExceeded
      return { ...usage[product], [USAGE_KB_KBS]: usage[KB]?.[USAGE_KB_KBS] }
    }
    return usage[product] || emptyObj
  }
)(byProductCacheKey)

const selectBillingUsageForProductFeature = createCachedSelector(
  selectBillingUsageForProduct,
  (_state, product) => product,
  (_state, _product, feature) => feature,
  (usage, product, feature) => {
    const key = billingUsageKeyForProductFeature(product, feature)
    return usage[key]
  }
)(byProductFeatureCacheKey)

const selectLocalUsage = createBasicSelector(
  selectWidgets,
  selectWidgetsBootstrapped,
  selectFolders,
  selectRulesTotal,
  selectCustomerRatingSettingsCountIfLoaded,
  selectKnowledgeBaseTotalOrNull,
  selectNonDeletedArticlesCount,
  selectCannedReplyTemplatesTotalCount,
  selectMailboxIds,
  selectInaccessibleMailboxesIds,
  selectWidgetsWithChat,
  selectGroups,
  selectDemoMailboxId,
  (
    widgets,
    isWidgetLoaded,
    folders,
    rulesCountIfLoaded,
    csatSettingsCountIfLoaded,
    kbCountIfLoaded,
    kbArticlesCount,
    cannedRepliesCountIfLoaded,
    mailboxIds,
    inaccessibleMailboxesIds,
    widgetsWithChat,
    teams,
    demoMailboxId
  ) => {
    const mailboxCount =
      (mailboxIds || []).length +
      (inaccessibleMailboxesIds || []).length -
      (demoMailboxId ? 1 : 0)
    const widgetsWithChatCount = (widgetsWithChat || []).length
    const foldersNonDefault = folders.filter(f => !f.default).length
    const csatSettingsEnabled = isDefined(csatSettingsCountIfLoaded)
      ? csatSettingsCountIfLoaded > 0
      : csatSettingsCountIfLoaded

    return {
      [KB]: {
        [USAGE_KB_KBS]: kbCountIfLoaded,
        // We can't use billing.usage.kb.articles state, because we need to include draft ones two
        [USAGE_KB_ARTICLES]: kbArticlesCount,
      },
      [INBOX]: {
        [USAGE_INBOX_CANNED_REPLIES]: cannedRepliesCountIfLoaded,
        [USAGE_INBOX_CHANNELS]: mailboxCount + widgetsWithChatCount,
        [USAGE_INBOX_MAILBOXES]: mailboxCount,
        [USAGE_INBOX_WIDGETS]: isWidgetLoaded ? widgets.length : null,
        [USAGE_INBOX_RULES]: rulesCountIfLoaded,
        [USAGE_INBOX_FOLDERS]: foldersNonDefault,
        [USAGE_INBOX_SATISFACTIONS_RATINGS]: csatSettingsEnabled,
        [USAGE_INBOX_TEAMS]: teams.length,
      },
    }
  }
)

export const selectLocalUsageForProduct = createCachedSelector(
  selectLocalUsage,
  (_state, product) => product,
  (usage, product) => usage[product] || emptyObj
)(byProductCacheKey)

const selectLocalUsageForProductFeature = createCachedSelector(
  selectLocalUsageForProduct,
  (_state, product) => product,
  (_state, _product, feature) => feature,
  (usage, product, feature) => {
    const key = billingUsageKeyForProductFeature(product, feature)
    return usage[key]
  }
)(byProductFeatureCacheKey)

export const selectUsageForProductFeature = createCachedSelector(
  selectBillingUsageForProductFeature,
  selectLocalUsageForProductFeature,
  (billingUsageAmount, localUsageAmount) => {
    // using local data first because we do not re-fetch billing usage after features have been added/deleted
    // this will be changing in the feature
    // would advice we send a realtime event when billing usage changes
    return isDefined(localUsageAmount) ? localUsageAmount : billingUsageAmount
  }
)(byProductFeatureCacheKey)

export const selectUsageForProduct = createCachedSelector(
  selectLocalUsageForProduct,
  selectBillingUsageForProduct,
  (localUsageForProduct, billingUsageForProduct) => {
    const usage = {}
    Object.keys(billingUsageForProduct).forEach(key => {
      usage[key] = isDefined(localUsageForProduct[key])
        ? localUsageForProduct[key]
        : billingUsageForProduct[key]
    })
    return usage
  }
)(byProductCacheKey)

export const selectCombinedRemoteLocalUsage = createSelector(
  selectBillingUsage,
  selectLocalUsage,
  (remoteUsage, localUsage) => {
    return merge(remoteUsage, removeNullValues(localUsage))
  }
)

export const selectShowBillingPlanUsageLimited = createSelector(
  selectCurrentUserIsAdminOrOwner,
  selectCurrentInbox,
  selectCurrentKb,
  selectCombinedRemoteLocalUsage,
  selectHasPendingDowngrade,
  selectPendingDowngradePlans,
  (
    isManager,
    currentInboxPlan,
    currentKbPlan,
    billingUsage,
    hasPendingDowngrade,
    pendingDowngradePlans
  ) => {
    return (
      hasPendingDowngrade &&
      isManager &&
      (isPlanLimitsExceeded(
        pendingDowngradePlans.inbox || currentInboxPlan,
        billingUsage.inbox
      ) ||
        isPlanLimitsExceeded(
          pendingDowngradePlans.kb || currentKbPlan,
          billingUsage.kb
        ))
    )
  }
)
